'use client';

import { useEffect, useMemo, useState } from 'react';

import type { Label } from './layout/legend/Legend';

export const chartColorPalette = [
  '#736185',
  '#4ecdc4',
  '#ffd166',
  '#ef476f',
  '#118ab2',
];

/**
 * Colour a series either with the one the caller picked or with the palette entry for its
 * position, wrapping around once there are more series than colours.
 */
export function seriesColor(color: string | undefined, index: number): string {
  return color || chartColorPalette[index % chartColorPalette.length]!;
}

export function toLegendLabels(
  dataset: { label: string; color?: string }[],
): Label[] {
  return dataset.map((data, index) => ({
    value: data.label,
    color: seriesColor(data.color, index),
  }));
}

export type ChartTheme = {
  background: string;
  primary: string;
  primaryShade: string;
  grid: string;
  fontFamily: string;
};

const emptyTheme: ChartTheme = {
  background: '',
  primary: '',
  primaryShade: '',
  grid: '',
  fontFamily: '',
};

export function readChartTheme(): ChartTheme {
  if (typeof document === 'undefined') {
    return emptyTheme;
  }

  const style = getComputedStyle(document.body);

  return {
    background: style.getPropertyValue('--color-background-main'),
    primary: style.getPropertyValue('--color-primary-main'),
    primaryShade: style.getPropertyValue('--color-primary-shade'),
    grid: style.getPropertyValue('--color-ring-light'),
    fontFamily: style.getPropertyValue('--font-family-large'),
  };
}

/**
 * The theme toggle swaps a `light`/`dark` class on the document element; nothing else in
 * the kit writes classes there. Track that class so charts can tell one theme from the
 * next.
 */
function useThemeRevision(): string {
  const [revision, setRevision] = useState(() =>
    typeof document === 'undefined' ? '' : document.documentElement.className,
  );

  useEffect(() => {
    const read = () => setRevision(document.documentElement.className);

    const observer = new MutationObserver(read);
    observer.observe(document.documentElement, { attributeFilter: ['class'] });

    // The first render may have run on the server, where there is no document to read.
    read();

    return () => observer.disconnect();
  }, []);

  return revision;
}

/**
 * A canvas keeps the colours it was painted with: unlike the DOM it does not repaint when
 * the CSS custom properties behind it change. Charts therefore depend on this snapshot,
 * which is renewed on every theme change so they are rebuilt in the new colours.
 */
export function useChartTheme(): ChartTheme {
  const revision = useThemeRevision();

  return useMemo(() => readChartTheme(), [revision]);
}

/**
 * Redraw key for charts that read custom properties of their own rather than the shared
 * `ChartTheme`. Include it in the effect that builds the chart.
 */
export function useThemeKey(): string {
  return useThemeRevision();
}

export function tooltipOptions(theme: ChartTheme) {
  return {
    displayColors: false,
    backgroundColor: theme.background,
    borderColor: theme.primary,
    borderWidth: 0,
    titleFont: {
      family: theme.fontFamily,
      size: 14,
    },
    bodyFont: {
      family: theme.fontFamily,
      size: 13,
    },
    footerFont: {
      family: theme.fontFamily,
      size: 13,
    },
    titleColor: theme.primaryShade,
    bodyColor: theme.primaryShade,
    footerColor: theme.primaryShade,
  };
}

export function tickOptions(theme: ChartTheme) {
  return {
    major: {
      enabled: true,
    },
    color: theme.primary,
    font: {
      family: theme.fontFamily,
      size: 14,
    },
  };
}

export function xAxisOptions(theme: ChartTheme) {
  return {
    ticks: tickOptions(theme),
    border: {
      color: theme.grid,
    },
    grid: {
      display: false,
      color: theme.grid,
    },
  };
}

export function yAxisOptions(theme: ChartTheme) {
  return {
    type: 'linear' as const,
    beginAtZero: true,
    ticks: tickOptions(theme),
    border: {
      display: false,
    },
    grid: {
      color: theme.grid,
    },
  };
}
